import { useMutation } from '@apollo/client/react';
import { Alert, Box, Button, Center, Icon, Input, InputProps, Link, Spinner, Stack, Text, Field } from '@chakra-ui/react';
import { FormContainer } from '@src/components/sco/FormContainer';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { LoginPayload, RegisterUserInput } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { GenericFormProps, processSubmit } from '@src/lib/form-helpers';
import { gql } from '@src/lib/gql';
import { useRouter } from 'next/navigation';
import { ReactNode, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { LuCheck, LuChevronRight } from 'react-icons/lu';
export type NewsletterSubscribeFormFields = {
  email?: string;
};
export type NewsletterSubscribeFormProps = GenericFormProps<NewsletterSubscribeFormFields> & {
  hubspotFormKey?: string;
  onLoginClick?: () => void;
  renderLogin?: () => ReactNode;
  defaultValues?: NewsletterSubscribeFormFields;
  title?: ReactNode;
};
export const NewsletterSubscribeForm = (props: NewsletterSubscribeFormProps) => {
  const router = useRouter();
  const {
    currentUser,
    refetchCurrentUser
  } = useCurrentUser();
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
  const [formSuccessMessage, setFormSuccessMessage] = useState<string>(null);
  const [formErrorMessage, setFormErrorMessage] = useState<string>(null);
  const form = useForm<NewsletterSubscribeFormFields>({
    defaultValues: props.defaultValues || {
      email: currentUser?.email
    },
    mode: 'onSubmit'
  });
  const [newsletterSignup] = useMutation(gql`
    mutation newsletterSignupMutation($input: NewsletterSignupInput!) {
      newsletterSignup(input: $input) {
        success
      }
    }
  `);
  const handleSubmit = useCallback(async (values: NewsletterSubscribeFormFields) => {
    const input: RegisterUserInput = {
      ...values,
      hubspotFormKey: props.hubspotFormKey
    };
    return processSubmit({
      props,
      values,
      setFormErrorMessage,
      action: async () => {
        return newsletterSignup({
          variables: {
            input
          }
        });
      },
      afterSuccess: async (_values, _result: LoginPayload) => {
        if (props.returnToHref) {
          setIsRedirecting(true);
          await refetchCurrentUser();
          router.push(props.returnToHref);
        } else {
          setFormSuccessMessage('Submitted • Thanks for subscribing!');
        }
      }
    });
  }, []);
  return <FormContainer {...props.containerProps}>
      {formSuccessMessage ? <Text textAlign={'center'}>
          <Icon asChild>
            <LuCheck />
          </Icon>{' '}
          {formSuccessMessage}
        </Text> : <>
          {isRedirecting && <Center padding={'1em'}>
              <Spinner />
            </Center>}
          {!isRedirecting && <Stack asChild align={'stretch'} direction={'column'} gap={'1em'}>
              <form onSubmit={form.handleSubmit(handleSubmit)}>
                {formErrorMessage && <Alert.Root borderRadius={'md'} status={'error'}>
                    <Alert.Indicator marginRight={'1em'} />
                    <Box textAlign={'left'}>{formErrorMessage}</Box>
                  </Alert.Root>}
                <Field.Root invalid={!!form.formState.errors.email}>
                  <Field.Label>Email Address</Field.Label>
                  <Input placeholder={'you@email.com'} size={'md'} type={'email'} {...props.inputProps as InputProps} {...form.register('email', {
              required: 'Email is required.',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              }
            })} />
                  <Field.ErrorText>{form.formState.errors.email?.message}</Field.ErrorText>
                </Field.Root>
                <Box>
                  <Button loading={form.formState.isSubmitting} size={'md'} type={'submit'} width={'full'} {...props.submitButtonProps}>
                    Join 250,000 Founders
                    <LuChevronRight />
                  </Button>
                </Box>
              </form>
            </Stack>}
          <Box paddingTop={'0.5em'}>
            <Text color={'colorPalette.fg/50'} fontSize={'11px'} textAlign={'center'}>
              {'Submission confirms agreement to our'}{' '}
              <Link asChild color={'colorPalette.fg/60'} variant={'underline'}>
                <NextLink href={'/legal/terms-of-service'} target={'_blank'}>
                  Terms of Service
                </NextLink>
              </Link>{' '}
              {'and'}{' '}
              <Link asChild color={'colorPalette.fg/60'} variant={'underline'}>
                <NextLink href={'/legal/privacy-policy'} target={'_blank'}>
                  Privacy Policy
                </NextLink>
              </Link>
              .
            </Text>
          </Box>
        </>}
    </FormContainer>;
};